import { useState, useEffect, useRef, useCallback, memo, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { X, AlertTriangle } from "lucide-react";
import { useLanguage } from "@/LanguageContext";
import { t } from "@/translations";
import { useSelection } from "@/SelectionContext";
import NoBlocksModal from "@/components/NoBlocksModal";
import GridNavigator from "@/components/GridNavigator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TOTAL_ROWS = 1000;
const TOTAL_COLS = 100;
const ROW_BUFFER = 15;
const FETCH_BUFFER = 50;

/* ── Tier Zone Utility ── */
const getTier = (row, col) => {
  if (row >= 0 && row <= 2 && col >= 0 && col <= 2) return 4;
  if (row >= 0 && row <= 2 && col >= 97 && col <= 99) return 4;
  if (row >= 97 && row <= 99 && col >= 0 && col <= 2) return 4;
  if (row >= 97 && row <= 99 && col >= 97 && col <= 99) return 4;
  if (row >= 997 && row <= 999 && col >= 0 && col <= 2) return 4;
  if (row >= 997 && row <= 999 && col >= 97 && col <= 99) return 4;
  if (row >= 44 && row <= 63 && col >= 44 && col <= 63) return 1;
  if (row >= 0 && row <= 14 && col >= 0 && col <= 14) return 2;
  if (row >= 0 && row <= 14 && col >= 85 && col <= 99) return 2;
  if (row >= 0 && row <= 14 && col >= 15 && col <= 84) return 3;
  return 0;
};

const TIER_NAMES = { 0: "", 1: "Center Stage", 2: "Corner Anchor", 3: "Top Band", 4: "Cluster" };

const PREDEFINED_CLUSTERS = [
  { startRow: 0, startCol: 0 },
  { startRow: 0, startCol: 97 },
  { startRow: 97, startCol: 0 },
  { startRow: 97, startCol: 97 },
  { startRow: 997, startCol: 0 },
  { startRow: 997, startCol: 97 },
];

const getPredefCluster = (row, col) => {
  for (const c of PREDEFINED_CLUSTERS) {
    if (row >= c.startRow && row <= c.startRow + 2 && col >= c.startCol && col <= c.startCol + 2) {
      const blocks = [];
      for (let r = c.startRow; r <= c.startRow + 2; r++)
        for (let cc = c.startCol; cc <= c.startCol + 2; cc++)
          blocks.push({ row: r, col: cc });
      return blocks;
    }
  }
  return null;
};

const isContiguous = (blockList) => {
  if (blockList.length === 0) return false;
  const set = new Set(blockList.map((b) => `${b.row}-${b.col}`));
  const visited = new Set();
  const queue = [blockList[0]];
  visited.add(`${blockList[0].row}-${blockList[0].col}`);
  while (queue.length > 0) {
    const curr = queue.shift();
    for (const [dr, dc] of [[-1,0],[1,0],[0,-1],[0,1]]) {
      const key = `${curr.row + dr}-${curr.col + dc}`;
      if (set.has(key) && !visited.has(key)) {
        visited.add(key);
        queue.push({ row: curr.row + dr, col: curr.col + dc });
      }
    }
  }
  return visited.size === blockList.length;
};

/* ── PixelCell Component ── */
const PixelCell = memo(({
  row, col, block, isSelected, isInSelection, tier,
  onMouseDown, onMouseEnter, onMouseUp, onHover, onLeave, touchFlipped,
}) => {
  const isSold = block && (block.status === "approved" || block.status === "courtesy") && (block.payment_status === "paid" || block.payment_status === "courtesy");
  const isPending = block && (block.status === "pending" || block.status === "pending_approval" || block.payment_status === "pending");
  const isAwaitingApproval = block && block.status === "pending_approval";
  const hasImage = isSold && block.image_url;

  const getImageStyle = () => {
    if (!hasImage) return {};
    const imgUrl = `${BACKEND_URL}${block.image_url}`;
    const meta = block.sprite_meta;
    if (meta && meta.num_cols > 1 || meta && meta.num_rows > 1) {
      const colOffset = col - meta.min_col;
      const rowOffset = row - meta.min_row;
      const bgWidth = meta.num_cols * 100;
      const bgHeight = meta.num_rows * 100;
      const posX = meta.num_cols > 1 ? (colOffset / (meta.num_cols - 1)) * 100 : 0;
      const posY = meta.num_rows > 1 ? (rowOffset / (meta.num_rows - 1)) * 100 : 0;
      return { backgroundImage: `url(${imgUrl})`, backgroundSize: `${bgWidth}% ${bgHeight}%`, backgroundPosition: `${posX}% ${posY}%` };
    }
    return { backgroundImage: `url(${imgUrl})`, backgroundSize: "cover", backgroundPosition: "center" };
  };

  const getBackStyle = () => {
    if (!hasImage) return {};
    const imgUrl = `${BACKEND_URL}${block.image_url}`;
    return { backgroundImage: `url(${imgUrl})`, backgroundSize: "contain", backgroundPosition: "center", backgroundRepeat: "no-repeat", backgroundColor: "#0F172A" };
  };

  const handleClick = (e) => { if (!isSold && !isPending) onMouseDown(row, col, e); };

  let cellClass = "pixel-cell";
  if (isSelected) cellClass += " selected";
  if (isInSelection) cellClass += " in-selection";
  if (isSold) cellClass += " sold";
  if (isPending) cellClass += " pending";
  if (touchFlipped) cellClass += " touch-flipped";

  return (
    <div
      className={cellClass}
      onMouseDown={handleClick}
      onMouseEnter={() => { onMouseEnter(row, col); onHover(row, col, block); }}
      onMouseUp={() => onMouseUp()}
      onMouseLeave={onLeave}
      role="button"
      aria-label={isSold ? `Block ${row + 1},${col + 1}: ${block?.slogan || "occupied"}` : `Block ${row + 1},${col + 1}: available`}
      data-testid={`cell-${row}-${col}`}
    >
      {hasImage ? (
        <>
          <div className="cell-inner">
            <div className="cell-front" style={getImageStyle()} />
            <div className="cell-back" style={getBackStyle()} />
          </div>
          {block.target_url && (
            <a href={block.target_url} target="_blank" rel="noopener noreferrer" className="absolute inset-0 z-10" onClick={(e) => e.stopPropagation()} aria-label={block.slogan || "Visit advertiser"} />
          )}
        </>
      ) : isAwaitingApproval ? (
        <div className="absolute inset-0 bg-[#334155] opacity-60" title="Awaiting Approval" />
      ) : (
        isSold && block.target_url && (
          <a href={block.target_url} target="_blank" rel="noopener noreferrer" className="absolute inset-0" onClick={(e) => e.stopPropagation()} aria-label={block.slogan || "Visit advertiser"} />
        )
      )}
    </div>
  );
});
PixelCell.displayName = "PixelCell";

/* ── Helper: read CSS --cell-size ── */
function getCellSize() {
  if (typeof window === "undefined") return 20;
  const val = getComputedStyle(document.documentElement).getPropertyValue("--cell-size").trim();
  return parseInt(val) || 20;
}

/* ── Main PixelGrid Component ── */
export const PixelGrid = ({ onSelectionChange, selectedBlocks = [], mode = "single" }) => {
  const { lang } = useLanguage();
  const { getBuyUrl } = useSelection();
  const navigate = useNavigate();

  const [blocks, setBlocks] = useState({});
  const [hoveredCell, setHoveredCell] = useState(null);
  const [hoveredBlock, setHoveredBlock] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [stats, setStats] = useState({ sold: 0, available: 100000, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [busyWarning, setBusyWarning] = useState(false);
  const [limitWarning, setLimitWarning] = useState(null);
  const [showNoBlocksModal, setShowNoBlocksModal] = useState(false);

  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [currentSelection, setCurrentSelection] = useState([]);

  const touchTimerRef = useRef(null);
  const touchStartPos = useRef(null);
  const isTouchSelecting = useRef(false);
  const [touchFlippedCell, setTouchFlippedCell] = useState(null);
  const [showTouchHint, setShowTouchHint] = useState(false);

  const [showHeatmap, setShowHeatmap] = useState(false);
  const [heatmapData, setHeatmapData] = useState({});
  const [heatmapMax, setHeatmapMax] = useState(1);
  const hoverBatchRef = useRef({});
  const hoverTimerRef = useRef(null);

  // Virtual scroll state
  const scrollContainerRef = useRef(null);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 60 });
  const [cellSize, setCellSize] = useState(20);
  const fetchedRangesRef = useRef(new Set());
  const fetchQueueRef = useRef(null);

  const isTouchDevice = typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0);

  const totalHeight = TOTAL_ROWS * cellSize;

  // Measure cell size from CSS
  useEffect(() => {
    const update = () => setCellSize(getCellSize());
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // Fetch blocks for a range and merge into cache
  const fetchBlockRange = useCallback(async (minRow, maxRow) => {
    const rangeKey = `${minRow}-${maxRow}`;
    if (fetchedRangesRef.current.has(rangeKey)) return;
    fetchedRangesRef.current.add(rangeKey);
    try {
      const res = await fetch(`${BACKEND_URL}/api/blocks/range?minRow=${minRow}&maxRow=${maxRow}&minCol=0&maxCol=99`);
      const data = await res.json();
      setBlocks((prev) => {
        const next = { ...prev };
        data.forEach((b) => { next[`${b.row}-${b.col}`] = b; });
        return next;
      });
    } catch (e) {
      fetchedRangesRef.current.delete(rangeKey);
    }
  }, []);

  // Initial load: fetch first visible chunk + stats
  useEffect(() => {
    const init = async () => {
      await Promise.all([
        fetchBlockRange(0, 80),
        fetch(`${BACKEND_URL}/api/stats`).then((r) => r.json()).then(setStats).catch(() => {}),
      ]);
      setLoading(false);
    };
    init();
    const interval = setInterval(() => {
      fetch(`${BACKEND_URL}/api/stats`).then((r) => r.json()).then(setStats).catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchBlockRange]);

  // On scroll: update visible range and fetch new blocks
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const scrollTop = el.scrollTop;
    const viewH = el.clientHeight;
    const start = Math.max(0, Math.floor(scrollTop / cellSize) - ROW_BUFFER);
    const end = Math.min(TOTAL_ROWS, Math.ceil((scrollTop + viewH) / cellSize) + ROW_BUFFER);
    setVisibleRange({ start, end });

    // Fetch blocks for visible area with buffer
    if (fetchQueueRef.current) cancelAnimationFrame(fetchQueueRef.current);
    fetchQueueRef.current = requestAnimationFrame(() => {
      const fetchStart = Math.max(0, start - FETCH_BUFFER);
      const fetchEnd = Math.min(TOTAL_ROWS - 1, end + FETCH_BUFFER);
      // Fetch in chunks of 100 rows
      const chunkSize = 100;
      for (let r = Math.floor(fetchStart / chunkSize) * chunkSize; r <= fetchEnd; r += chunkSize) {
        fetchBlockRange(r, Math.min(r + chunkSize - 1, TOTAL_ROWS - 1));
      }
    });
  }, [cellSize, fetchBlockRange]);

  // Heatmap
  const fetchHeatmap = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/heatmap`);
      const data = await res.json();
      const map = {};
      let max = 1;
      data.forEach((d) => { map[`${d.row}-${d.col}`] = d.count; if (d.count > max) max = d.count; });
      setHeatmapData(map);
      setHeatmapMax(max);
    } catch {}
  }, []);

  const flushHoverBatch = useCallback(() => {
    const cells = Object.keys(hoverBatchRef.current).map((k) => {
      const [r, c] = k.split("-").map(Number);
      return { row: r, col: c };
    });
    if (cells.length === 0) return;
    hoverBatchRef.current = {};
    fetch(`${BACKEND_URL}/api/heatmap/track`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ cells }),
    }).catch(() => {});
  }, []);

  const recordHover = useCallback((row, col) => {
    hoverBatchRef.current[`${row}-${col}`] = true;
    if (!hoverTimerRef.current) {
      hoverTimerRef.current = setTimeout(() => { flushHoverBatch(); hoverTimerRef.current = null; }, 5000);
    }
  }, [flushHoverBatch]);

  useEffect(() => { if (showHeatmap) fetchHeatmap(); }, [showHeatmap, fetchHeatmap]);
  useEffect(() => () => { if (hoverTimerRef.current) { clearTimeout(hoverTimerRef.current); flushHoverBatch(); } }, [flushHoverBatch]);

  // Selection logic
  const isCellBusy = useCallback((row, col) => {
    const b = blocks[`${row}-${col}`];
    return b && (b.status === "approved" || b.status === "pending" || b.status === "pending_approval" || b.status === "courtesy");
  }, [blocks]);

  const calculateSelection = useCallback((start, end) => {
    if (!start || !end) return [];
    const minR = Math.min(start.row, end.row), maxR = Math.max(start.row, end.row);
    const minC = Math.min(start.col, end.col), maxC = Math.max(start.col, end.col);
    const sel = [];
    for (let r = minR; r <= maxR; r++)
      for (let c = minC; c <= maxC; c++)
        sel.push({ row: r, col: c });
    return sel;
  }, []);

  const showLimitPopup = useCallback((msg) => {
    setLimitWarning(msg);
    setTimeout(() => setLimitWarning(null), 3000);
  }, []);

  const validateAndCommit = useCallback((selection) => {
    const busyCells = selection.filter((s) => isCellBusy(s.row, s.col));
    if (busyCells.length > 0) {
      setBusyWarning(true);
      showLimitPopup(`${busyCells.length} ${t("grid.blocksSoldPending", lang)}`);
      return;
    }
    setBusyWarning(false);
    if (selection.length > 9) { showLimitPopup(t("grid.maxBlocksPolicy", lang)); return; }
    if (mode === "cluster") {
      if (selection.length < 7 || selection.length > 9) { showLimitPopup(t("grid.clusterMustBe7_9", lang)); return; }
      if (!isContiguous(selection)) { showLimitPopup(t("grid.mustSelectAdjacentCluster", lang)); return; }
    }
    onSelectionChange?.(selection);
  }, [isCellBusy, mode, onSelectionChange, showLimitPopup]);

  const handleMouseDown = useCallback((row, col, e) => {
    if (isCellBusy(row, col)) { showLimitPopup(t("grid.alreadySoldOrPending", lang)); return; }
    if (mode === "cluster") {
      const predef = getPredefCluster(row, col);
      if (predef) { validateAndCommit(predef); return; }
      setIsSelecting(true); setSelectionStart({ row, col }); setCurrentSelection([{ row, col }]);
    } else if (mode === "multi") {
      setIsSelecting(true); setSelectionStart({ row, col }); setCurrentSelection([{ row, col }]);
    } else {
      validateAndCommit([{ row, col }]);
    }
  }, [mode, isCellBusy, validateAndCommit]);

  const handleMouseEnter = useCallback((row, col) => {
    if (isSelecting && (mode === "multi" || mode === "cluster")) {
      setCurrentSelection(calculateSelection(selectionStart, { row, col }));
    }
  }, [isSelecting, mode, selectionStart, calculateSelection]);

  const handleMouseUp = useCallback(() => {
    if (isSelecting && (mode === "multi" || mode === "cluster")) {
      setIsSelecting(false);
      if (currentSelection.length > 0) validateAndCommit(currentSelection);
    }
  }, [isSelecting, mode, currentSelection, validateAndCommit]);

  useEffect(() => {
    const up = () => { if (isSelecting) handleMouseUp(); };
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }, [isSelecting, handleMouseUp]);

  const handleHover = useCallback((row, col, block) => {
    setHoveredCell({ row, col }); setHoveredBlock(block); recordHover(row, col);
  }, [recordHover]);

  const handleMouseMove = useCallback((e) => { setMousePos({ x: e.clientX, y: e.clientY }); }, []);
  const handleLeave = useCallback(() => { setHoveredCell(null); setHoveredBlock(null); }, []);

  // Touch: compute cell from coordinates
  const getCellFromPoint = useCallback((clientX, clientY) => {
    const el = scrollContainerRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const scrollLeft = el.scrollLeft;
    const scrollTop = el.scrollTop;
    const x = clientX - rect.left + scrollLeft;
    const y = clientY - rect.top + scrollTop;
    const col = Math.floor(x / cellSize);
    const row = Math.floor(y / cellSize);
    if (row < 0 || row >= TOTAL_ROWS || col < 0 || col >= TOTAL_COLS) return null;
    return { row, col };
  }, [cellSize]);

  const showHintOnce = useCallback(() => {
    if (localStorage.getItem("vp_touch_hint_shown")) return;
    setShowTouchHint(true);
    localStorage.setItem("vp_touch_hint_shown", "1");
    setTimeout(() => setShowTouchHint(false), 4000);
  }, []);

  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    const cell = getCellFromPoint(touch.clientX, touch.clientY);
    if (!cell) return;
    touchStartPos.current = { x: touch.clientX, y: touch.clientY, row: cell.row, col: cell.col };
    isTouchSelecting.current = false;
    if (touchTimerRef.current) clearTimeout(touchTimerRef.current);
    const block = blocks[`${cell.row}-${cell.col}`];
    const cellBusy = isCellBusy(cell.row, cell.col);
    touchTimerRef.current = setTimeout(() => {
      if (!touchStartPos.current) return;
      if (cellBusy && block) {
        setTouchFlippedCell(`${cell.row}-${cell.col}`);
        setHoveredCell(cell); setHoveredBlock(block);
        setMousePos({ x: touch.clientX, y: touch.clientY });
      } else {
        // Long press always activates multi-select (regardless of mode)
        isTouchSelecting.current = true;
        setIsSelecting(true); setSelectionStart(cell); setCurrentSelection([cell]);
        if (scrollContainerRef.current) scrollContainerRef.current.classList.add("touch-selecting");
        if (navigator.vibrate) navigator.vibrate(30);
      }
    }, 500);
  }, [blocks, isCellBusy, getCellFromPoint, mode]);

  const handleTouchMove = useCallback((e) => {
    if (!touchStartPos.current) return;
    const touch = e.touches[0];
    const dx = Math.abs(touch.clientX - touchStartPos.current.x);
    const dy = Math.abs(touch.clientY - touchStartPos.current.y);
    if (!isTouchSelecting.current && (dx > 10 || dy > 10)) {
      if (touchTimerRef.current) { clearTimeout(touchTimerRef.current); touchTimerRef.current = null; }
      return;
    }
    if (isTouchSelecting.current) {
      e.preventDefault();
      const cell = getCellFromPoint(touch.clientX, touch.clientY);
      if (cell) setCurrentSelection(calculateSelection(touchStartPos.current, cell));
    }
  }, [getCellFromPoint, calculateSelection]);

  const handleTouchEnd = useCallback((e) => {
    if (touchTimerRef.current) { clearTimeout(touchTimerRef.current); touchTimerRef.current = null; }
    if (touchFlippedCell) {
      setTimeout(() => { setTouchFlippedCell(null); setHoveredCell(null); setHoveredBlock(null); }, 800);
    }
    if (scrollContainerRef.current) scrollContainerRef.current.classList.remove("touch-selecting");
    if (isTouchSelecting.current) {
      isTouchSelecting.current = false; setIsSelecting(false);
      if (currentSelection.length > 0) validateAndCommit(currentSelection);
      return;
    }
    if (touchStartPos.current) {
      const touch = e.changedTouches[0];
      const dx = Math.abs(touch.clientX - touchStartPos.current.x);
      const dy = Math.abs(touch.clientY - touchStartPos.current.y);
      if (dx < 10 && dy < 10) {
        const { row, col } = touchStartPos.current;
        if (!isCellBusy(row, col)) {
          validateAndCommit([{ row, col }]);
          if (mode !== "single") showHintOnce();
        } else {
          const block = blocks[`${row}-${col}`];
          if (block) {
            setTouchFlippedCell(`${row}-${col}`);
            setHoveredCell({ row, col }); setHoveredBlock(block);
            setMousePos({ x: touch.clientX, y: touch.clientY });
            setTimeout(() => { setTouchFlippedCell(null); setHoveredCell(null); setHoveredBlock(null); }, 1500);
          }
        }
      }
    }
    touchStartPos.current = null; isTouchSelecting.current = false;
  }, [touchFlippedCell, currentSelection, validateAndCommit, isCellBusy, blocks, mode, showHintOnce]);

  const isInCurrentSelection = useCallback((row, col) => currentSelection.some((s) => s.row === row && s.col === col), [currentSelection]);
  const isBlockSelected = useCallback((row, col) => selectedBlocks.some((s) => s.row === row && s.col === col), [selectedBlocks]);

  // Double-tap outside grid resets zoom — uses CSS transform approach for reliability
  useEffect(() => {
    let lastTap = 0;
    const handleDblTap = (e) => {
      const gridEl = scrollContainerRef.current;
      if (gridEl && gridEl.contains(e.target)) return;
      const now = Date.now();
      if (now - lastTap < 350) {
        // Double-tap detected outside grid — reset zoom via viewport meta
        const vp = document.querySelector('meta[name="viewport"]');
        if (vp) {
          vp.setAttribute("content", "width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1");
          requestAnimationFrame(() => {
            setTimeout(() => {
              vp.setAttribute("content", "width=device-width, initial-scale=1, user-scalable=yes");
            }, 100);
          });
        }
      }
      lastTap = now;
    };
    const handleDblClick = (e) => {
      const gridEl = scrollContainerRef.current;
      if (gridEl && gridEl.contains(e.target)) return;
      const vp = document.querySelector('meta[name="viewport"]');
      if (vp) {
        vp.setAttribute("content", "width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1");
        requestAnimationFrame(() => {
          setTimeout(() => {
            vp.setAttribute("content", "width=device-width, initial-scale=1, user-scalable=yes");
          }, 100);
        });
      }
    };
    document.addEventListener("touchend", handleDblTap, { passive: true });
    document.addEventListener("dblclick", handleDblClick);
    return () => {
      document.removeEventListener("touchend", handleDblTap);
      document.removeEventListener("dblclick", handleDblClick);
    };
  }, []);

  // Build visible rows
  const visibleCells = useMemo(() => {
    const rows = [];
    for (let row = visibleRange.start; row < visibleRange.end; row++) {
      const rowCells = [];
      for (let col = 0; col < TOTAL_COLS; col++) {
        const key = `${row}-${col}`;
        rowCells.push(
          <PixelCell
            key={key}
            row={row}
            col={col}
            block={blocks[key]}
            isSelected={isBlockSelected(row, col)}
            isInSelection={isSelecting && isInCurrentSelection(row, col)}
            tier={getTier(row, col)}
            onMouseDown={handleMouseDown}
            onMouseEnter={handleMouseEnter}
            onMouseUp={handleMouseUp}
            onHover={handleHover}
            onLeave={handleLeave}
            touchFlipped={touchFlippedCell === key}
          />
        );
      }
      rows.push(
        <div
          key={row}
          className="virtual-row"
          style={{
            position: "absolute",
            top: row * cellSize,
            left: 0,
            display: "grid",
            gridTemplateColumns: `repeat(${TOTAL_COLS}, ${cellSize}px)`,
            height: cellSize,
            width: TOTAL_COLS * cellSize,
          }}
        >
          {rowCells}
        </div>
      );
    }
    return rows;
  }, [visibleRange, blocks, isBlockSelected, isSelecting, isInCurrentSelection, touchFlippedCell,
      cellSize, handleMouseDown, handleMouseEnter, handleMouseUp, handleHover, handleLeave]);

  // Heatmap overlay for visible rows
  const heatmapOverlay = useMemo(() => {
    if (!showHeatmap) return null;
    const cells = [];
    for (let row = visibleRange.start; row < visibleRange.end; row++) {
      for (let col = 0; col < TOTAL_COLS; col++) {
        const count = heatmapData[`${row}-${col}`] || 0;
        if (count === 0) continue;
        const ratio = Math.min(count / heatmapMax, 1);
        let bgColor;
        if (ratio < 0.33) bgColor = `rgba(59,130,246,${0.15 + ratio * 0.6})`;
        else if (ratio < 0.66) bgColor = `rgba(34,197,94,${0.2 + ratio * 0.5})`;
        else bgColor = `rgba(239,68,68,${0.3 + ratio * 0.5})`;
        cells.push(
          <div
            key={`hm-${row}-${col}`}
            style={{
              position: "absolute",
              top: row * cellSize,
              left: col * cellSize,
              width: cellSize,
              height: cellSize,
              backgroundColor: bgColor,
              pointerEvents: "none",
            }}
          />
        );
      }
    }
    return cells;
  }, [showHeatmap, visibleRange, heatmapData, heatmapMax, cellSize]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="spinner mb-4" />
        <p className="font-pixel text-[#64748B]">{t("grid.loading", lang)}</p>
      </div>
    );
  }

  const hoveredTier = hoveredCell ? getTier(hoveredCell.row, hoveredCell.col) : 0;
  const gridWidth = TOTAL_COLS * cellSize;

  return (
    <div className="flex flex-col items-center w-full">
      {/* Stats */}
      <div className="flex flex-wrap gap-4 mb-6 justify-center">
        <div className="stats-badge" data-testid="sold-count">
          <span className="text-[#14B8A6]">{t("grid.sold", lang)}:</span> {stats.sold}
        </div>
        <div className="stats-badge" data-testid="available-count">
          <span className="text-[#F59E0B]">{t("grid.available", lang)}:</span> {stats.available}
        </div>
        {stats.pending > 0 && (
          <div className="stats-badge" data-testid="pending-count">
            <span className="text-[#0EA5E9]">{t("grid.pending", lang)}:</span> {stats.pending}
          </div>
        )}
      </div>

      {/* Mode indicator */}
      {mode === "multi" && (
        <div className="mb-4 p-3 bg-[#14B8A6]/10 border-2 border-[#14B8A6] text-center">
          <p className="font-pixel text-sm text-[#14B8A6]">{t("grid.multiMode", lang)}</p>
        </div>
      )}
      {mode === "cluster" && (
        <div className="mb-4 p-3 bg-[#F59E0B]/10 border-2 border-[#F59E0B] text-center" data-testid="cluster-mode-indicator">
          <p className="font-pixel text-sm text-[#F59E0B]">{t("grid.clusterMode", lang)}</p>
        </div>
      )}

      {/* Tier legend */}
      <div className="flex flex-wrap gap-3 mb-4 justify-center">
        {[
          { tier: 1, label: t("grid.tier1", lang), color: "#F59E0B" },
          { tier: 2, label: t("grid.tier2", lang), color: "#0EA5E9" },
          { tier: 3, label: t("grid.tier3", lang), color: "#FF6B6B" },
          { tier: 4, label: t("grid.tier4", lang), color: "#14B8A6" },
        ].map(({ tier, label, color }) => (
          <div key={tier} className="flex items-center gap-1.5 text-xs font-pixel" data-testid={`tier-legend-${tier}`}>
            <div className="w-3 h-3 border border-gray-300" style={{ backgroundColor: color, opacity: 0.7 }} />
            <span className="text-[#64748B]">{label}</span>
          </div>
        ))}
      </div>

      {/* Heatmap toggle */}
      <div className="mb-4 flex justify-center">
        <button
          onClick={() => setShowHeatmap(!showHeatmap)}
          className={`font-pixel text-xs px-4 py-2 border-2 transition-colors ${showHeatmap ? "bg-[#FF6B6B] border-[#FF6B6B] text-white" : "bg-transparent border-[#64748B] text-[#64748B] hover:border-[#FF6B6B] hover:text-[#FF6B6B]"}`}
          data-testid="heatmap-toggle-btn"
        >
          {showHeatmap ? t("heatmap.hide", lang) : t("heatmap.show", lang)}
        </button>
      </div>
      {showHeatmap && (
        <div className="mb-3 flex items-center justify-center gap-2 text-xs font-pixel text-[#64748B]">
          <span>{t("heatmap.low", lang)}</span>
          <div className="w-24 h-3" style={{ background: "linear-gradient(to right, rgba(59,130,246,0.3), rgba(34,197,94,0.4), rgba(245,158,11,0.5), rgba(239,68,68,0.7))" }} />
          <span>{t("heatmap.high", lang)}</span>
        </div>
      )}

      {/* Grid row counter */}
      <div className="mb-2 text-xs font-pixel text-[#64748B]">
        {TOTAL_ROWS} {t("grid.rowsLabel", lang)} &times; {TOTAL_COLS} {t("grid.colsLabel", lang)} &mdash; {(TOTAL_ROWS * TOTAL_COLS).toLocaleString()} {t("grid.blocksLabel", lang)}
      </div>

      {/* Virtual Scroll Grid Container + Navigator */}
      <div className="grid-with-nav self-stretch">
        <div
          ref={scrollContainerRef}
          className="grid-container pixel-grid-container"
          style={{ userSelect: "none" }}
          onScroll={handleScroll}
          onMouseMove={handleMouseMove}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className="virtual-grid-inner"
            style={{
              position: "relative",
              width: gridWidth + 8,
              height: totalHeight,
              border: "4px solid #0F172A",
              boxShadow: "8px 8px 0px 0px #0F172A",
              background: "#FFFFFF",
              margin: "0 auto",
            }}
          >
            {visibleCells}
            {heatmapOverlay}
          </div>
        </div>
        <GridNavigator
          scrollContainerRef={scrollContainerRef}
          visibleRange={visibleRange}
          blocks={blocks}
          cellSize={cellSize}
          totalRows={TOTAL_ROWS}
          totalCols={TOTAL_COLS}
        />
      </div>

      <p className="text-xs text-[#64748B] mt-2 text-center sm:hidden font-pixel">
        {mode !== "single" ? t("grid.longPressDrag", lang) : t("grid.scrollToExplore", lang)}
      </p>

      {showTouchHint && (
        <div className="touch-hint" data-testid="touch-hint">
          {t("grid.longPressHint", lang)}
        </div>
      )}

      {/* Floating Tooltip */}
      {hoveredCell && (
        <div
          className="cell-tooltip"
          data-testid="grid-tooltip"
          style={{
            position: "fixed",
            left: `${Math.min(mousePos.x + 16, window.innerWidth - 290)}px`,
            top: `${Math.max(mousePos.y - 50, 10)}px`,
            zIndex: 1000,
          }}
        >
          {hoveredBlock && (hoveredBlock.status === "approved" || hoveredBlock.status === "courtesy") ? (
            <div>
              <p className="font-bold">{hoveredBlock.slogan}</p>
              <p className="text-xs text-gray-400 mt-1">{t("grid.clickToVisit", lang)}</p>
            </div>
          ) : hoveredBlock && (hoveredBlock.status === "pending" || hoveredBlock.status === "pending_approval") ? (
            <span className="text-[#F59E0B]">{t("grid.awaitingApproval", lang)}</span>
          ) : (
            <span>
              {t("grid.row", lang)} {hoveredCell.row + 1}, {t("grid.col", lang)} {hoveredCell.col + 1} - {t("grid.available", lang)}
              {hoveredTier > 0 && <span className="ml-1 text-[#F59E0B]">({
                hoveredTier === 1 ? t("grid.centerStageShort", lang) :
                hoveredTier === 2 ? t("grid.cornerAnchorShort", lang) :
                hoveredTier === 3 ? t("grid.topBandShort", lang) :
                t("grid.clusterShort", lang)
              })</span>}
            </span>
          )}
        </div>
      )}

      {busyWarning && (
        <div className="mt-4 p-3 bg-[#FF6B6B]/10 border-2 border-[#FF6B6B] text-center" data-testid="busy-warning">
          <p className="font-pixel text-sm text-[#FF6B6B]">{t("grid.busyWarning", lang)}</p>
        </div>
      )}

      {/* Floating Selection Toolbar */}
      {selectedBlocks.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] animate-slide-up" data-testid="selection-toolbar">
          <div className="bg-[#0F172A]/95 backdrop-blur-xl border-2 border-[#14B8A6] px-6 py-3 flex items-center gap-5 shadow-2xl shadow-[#14B8A6]/20" style={{ borderRadius: "2px" }}>
            <div className="text-white font-pixel text-sm whitespace-nowrap" data-testid="toolbar-info">
              <span className="text-[#14B8A6] font-bold text-base">{selectedBlocks.length}</span>{" "}
              {selectedBlocks.length === 1 ? t("toolbar.selected", lang) : t("toolbar.selectedPlural", lang)}
              <span className="mx-2 text-[#64748B]">—</span>
              <span className="text-[#94A3B8]">{lang === "en" ? "from" : "desde"} </span>
              <span className="text-[#F59E0B] font-bold text-base">${selectedBlocks.length * 100}</span>
              <span className="text-[#94A3B8]">{t("toolbar.perYear", lang)}</span>
            </div>
            <button onClick={() => navigate(getBuyUrl())} className="relative bg-[#14B8A6] text-white font-pixel text-sm px-5 py-2 font-bold transition-all hover:bg-[#0D9488] active:scale-95" data-testid="toolbar-buy-btn">
              <span className="absolute inset-0 bg-[#14B8A6] animate-ping opacity-30" style={{ borderRadius: "2px" }} />
              <span className="relative">{t("toolbar.buyNow", lang)}</span>
            </button>
            <button onClick={() => onSelectionChange([])} className="text-[#64748B] hover:text-[#FF6B6B] transition-colors font-pixel text-xs" data-testid="toolbar-clear-btn" title={t("toolbar.clear", lang)}>
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 text-center">
        <p className="font-pixel text-sm text-[#64748B] mb-3" data-testid="no-selection-hint">{t("grid.selectHint", lang)}</p>
        <button onClick={() => selectedBlocks.length > 0 ? navigate(getBuyUrl()) : setShowNoBlocksModal(true)} className="btn-primary inline-block" data-testid="buy-pixels-cta-btn">{t("grid.buyPixelsCta", lang)}</button>
      </div>
      <NoBlocksModal isOpen={showNoBlocksModal} onClose={() => setShowNoBlocksModal(false)} />
      {/* Centered limit warning popup */}
      {limitWarning && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(2px)" }}
          onClick={() => setLimitWarning(null)}
          data-testid="limit-warning-overlay"
        >
          <div
            className="bg-[#1E293B] border-2 border-[#F59E0B] p-6 max-w-sm w-[90%] text-center"
            onClick={(e) => e.stopPropagation()}
            data-testid="limit-warning-popup"
          >
            <AlertTriangle size={36} className="text-[#F59E0B] mx-auto mb-3" />
            <p className="font-heading font-bold text-white text-sm leading-relaxed">{limitWarning}</p>
            <p className="text-[#64748B] text-xs mt-3">{t("grid.clickToDismiss", lang)}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PixelGrid;
